import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../../../assignments/assignments.scss';
import {
  useGetPreviewActivityInfoMutation,
  useMoveVocabularyMutation,
  useReorderVocabularyMutation,
  useRemoveVocabularyMutation
} from '../../../../features/authentication/activityApiSlice';
import AssignmentPreviewHeader from '../../../assignments/components/AssignmentPreviewHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faInfo,
  faUserGraduate
} from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import EditAssignmentOptionsModal from './Components/EditAssignmentOptionsModal';
import VocabularyTerms from './Components/VocabularyTerms';
import ConfirmationModal from '../../../../components/ConfirmationPopUp';
import QuestionPreviewTypes from '../../../../enums/questionPreviewTypes';
import { DateFormatter } from '../../../../utils/DateFormatter';
import CustomToast from '../../../../components/Toast';
import ActivityTypes from '../../../../enums/activityTypes';
import PropTypes from 'prop-types';

const QUESTIONS_PER_PAGE = 10;
const CONFIRM_ACTIONS = {
  DELETE: 'delete',
  SHUFFLE: 'shuffle'
};

const EditActivity= ({
  isPreview = false
}) =>  {
  const { Id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [getPreviewActivityInfo, { isLoading: isLoadingPreview }] = useGetPreviewActivityInfoMutation();
  const [reorderVocabulary, { isLoading: isLoadingReorder }] = useReorderVocabularyMutation();
  const [moveVocabulary, { isLoading: isLoadingMove }] = useMoveVocabularyMutation();
  const [removeVocabulary, { isLoading: isLoadingRemove }] = useRemoveVocabularyMutation();
  const [questions, setQuestions] = useState({});
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [currentQuestionId, setCurrentQuestionId] = useState(0);
  const [questionCourseId, setQuestionCourseId] = useState(null);
  const [isBlockedQuestion, setIsBlockedQuestion] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedMcStyle, setSelectedMcStyle] = useState('');
  const [confirmModel, setConfirmModel] = useState(false);
  const [confirmModelSelected, setConfirmModelSelected] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dropIndex, setDropIndex] = useState(null);
  const assignedStatus = questions?.assignedStatus;
  const mcChoices = location.state?.mcChoices;

  const questionIds = useMemo(() => questions?.questionIds ?? [], [questions]);
  const questionCount = questionIds.length;
  const numPages = useMemo(
    () => Math.ceil(questionCount / QUESTIONS_PER_PAGE),
    [questionCount]
  );
  const startIndex = currentPage * QUESTIONS_PER_PAGE;
  const endIndex = startIndex + QUESTIONS_PER_PAGE;
  const pageQuestionIds = useMemo(
    () => questionIds.slice(startIndex, endIndex),
    [endIndex, questionIds, startIndex]
  );

  const vocabMap = useMemo(
    () =>
      questions?.vocabulary?.reduce((acc, vocab) => {
        acc[vocab.id] = vocab;
        return acc;
      }, {}) ?? {},
    [questions]
  );

  const selectedQuestionId = questionIds[selectedQuestionIndex];
  const selectedVocab = vocabMap[selectedQuestionId];

  const syncSelectedQuestion = useCallback(
    (index) => {
      const boundedIndex = Math.max(0, Math.min(index, Math.max(questionCount - 1, 0)));
      setSelectedQuestionIndex(boundedIndex);

      const questionId = questionIds[boundedIndex];
      const vocab = vocabMap[questionId];
      setQuestionCourseId(questions?.courseId ?? null);

      if (vocab) {
        setCurrentQuestionId(vocab.id);
        setIsBlockedQuestion(false);
      } else {
        setCurrentQuestionId(0);
      }
    },
    [questionCount, questionIds, questions, vocabMap]
  );

  const getPreviewHandler = useCallback(async () => {
    const option = { assignmentId: Id };
    try {
      const response = await getPreviewActivityInfo(option).unwrap();
      setSelectedMcStyle(response.mcStyle);
      setQuestions(response);
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [Id, getPreviewActivityInfo]);

  const getQuestionDetailsHandler = useCallback(
    async (index) => {
      
      syncSelectedQuestion(index);
      
    },
    [syncSelectedQuestion]
  );

  useEffect(() => {
    getPreviewHandler();
  }, [getPreviewHandler]);

  useEffect(() => {
    if (questionCount === 0) {
      setSelectedQuestionIndex(0);
      setCurrentQuestionId(0);
      setQuestionCourseId(questions?.courseId ?? null);
      return;
    }

    if (selectedQuestionIndex > questionCount - 1) {
      syncSelectedQuestion(questionCount - 1);
      return;
    }

    syncSelectedQuestion(selectedQuestionIndex);
  }, [questionCount, questions, selectedQuestionIndex, syncSelectedQuestion]);

  useEffect(() => {
    if (numPages === 0) {
      setCurrentPage(0);
      return;
    }

    if (currentPage > numPages - 1) {
      setCurrentPage(numPages - 1);
    }
  }, [currentPage, numPages]);

  function handlePrevPage(isLastQuestion = false) {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 0));
    if (!isDragging && !isLastQuestion) {
      syncSelectedQuestion(selectedQuestionIndex);
    }
  }

  function handleNextPage() {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, Math.max(numPages - 1, 0)));
    if (!isDragging) {
      syncSelectedQuestion(selectedQuestionIndex);
    }
  }

  const onDragStart = (event, index, questionIndex) => {
    event.dataTransfer.setData('text/plain', index);
    if (selectedQuestionIndex === questionIndex)
      setIsDragging(true);
    else
      setIsDragging(false);
  };

  const onDragOver = (event, index) => {
    event.preventDefault();
    setDropIndex(index);
  };

  const onDragLeave = () => {
    setDropIndex(null);
  };

  const onDrop = (event, index) => {
    event.preventDefault();
    if (selectedQuestionIndex + 1 !== index)
      handleMoveQuestion(selectedQuestionIndex + 1, index);
    setDropIndex(null);
    setIsDragging(false);
  };

  const handleMoveQuestion = async (currentPosition, newPosition) => {
    if (questionCount > 1) {
      
      const option = {
        assignmentId: Id,
        currentPosition,
        newPosition
      };
      try {
        const response = await moveVocabulary(option).unwrap();
        if (response) {
          CustomToast('Vocabulary Moved Successfully', 'success');
          await getPreviewHandler();
          syncSelectedQuestion(newPosition - 1);
        } else {
          CustomToast('Failed to Move Vocabulary', 'error');
        }
      } catch (error) {
        console.error(error);
        CustomToast(error?.data?.error ?? 'Failed to Move Vocabulary', 'error');
      } 
    }
  };

  const handleRemoveQuestion = async () => {
    setConfirmModel(false);
    const option = {
      assignmentId: Id,
      vocabularyNo: selectedQuestionIndex + 1
    };
    try {
      await removeVocabulary(option).unwrap();
      
      if (questionCount === selectedQuestionIndex + 1) {
        syncSelectedQuestion(selectedQuestionIndex - 1);
      } else {
        syncSelectedQuestion(selectedQuestionIndex);
      }
      await getPreviewHandler();
      CustomToast('Vocabulary Deleted Successfully', 'success');

      if (pageQuestionIds.length === 1) {
        handlePrevPage(true);
        syncSelectedQuestion(selectedQuestionIndex - 1);
      }
    } catch (error) {
      console.error(error);
      CustomToast(error?.data?.error ?? 'Failed to Delete Vocabulary', 'error');
    }
  };

  const handleReorderQuestion = async () => {
    setConfirmModel(false);
    if (questionCount > 1) {
      
      const option = {
        assignmentId: Id
      };
      try {
        const response = await reorderVocabulary(option).unwrap();
        if (response) {
          CustomToast('Vocabulary Shuffled Successfully', 'success');
          await getPreviewHandler();
          syncSelectedQuestion(selectedQuestionIndex);
        } else {
          CustomToast('Vocabulary Shuffle Failed', 'error');
        }
      } catch (error) {
        console.error(error);
        CustomToast(error?.data?.error ?? 'Vocabulary Shuffle Failed', 'error');
      }
    }
  };

  const handleNextVocabulary = () => {
    const nextIndex = selectedQuestionIndex + 1 >= questionCount ? 0 : selectedQuestionIndex + 1;

    // If next item is on a different page, update page first
    const nextPage = Math.floor(nextIndex / QUESTIONS_PER_PAGE);
    if (nextPage !== currentPage) {
      setCurrentPage(nextPage);
    }

    getQuestionDetailsHandler(nextIndex);
  };

  const handleAssignToStudents = () => {
    navigate(`/assignments/${Id}/${ActivityTypes.FlashCard}/manage_assign/assign_to_students`);
  };

  return (
    <>
      <div>
        <>
          <div>
            <AssignmentPreviewHeader
              assignmentName={questions?.assignmentName}
              numberOfQuestions={`${questionCount > 0 ? `${selectedQuestionIndex + 1}/${questionCount}` : 0}`}
              courseName={questions?.courseName}
              verificationNumber={questions?.verificationNumber}
              type={isPreview ? QuestionPreviewTypes.viewAssignmentPreview : QuestionPreviewTypes.editAssignmentPreview }
              createdDate={questions?.createdDate !== undefined ? DateFormatter.toShortDate(questions?.createdDate) : ''}
              isFlashcard={true}
            />
          </div>
    
          <div className="assignment-review-content d-flex align-items-start mb-2 gap-4 mt-3">
            <div className="questions-numbers edit flex-shrink-0">
              <div className="border-grey white-card">
                <VocabularyTerms
                  questions={questions}
                  assignedStatus={assignedStatus}
                  questionCount={questionCount}
                  selectedQuestionIndex={selectedQuestionIndex}
                  pageQuestionIds={pageQuestionIds}
                  numPages={numPages}
                  currentPage={currentPage}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  isDragging={isDragging}
                  dropIndex={dropIndex}
                  assignmentId={Id}
                  onGetQuestionDetails={getQuestionDetailsHandler}
                  onPrevPage={handlePrevPage}
                  onNextPage={handleNextPage}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  onDragLeave={onDragLeave}
                  onDeleteClick={() => {
                    setConfirmModelSelected(CONFIRM_ACTIONS.DELETE);
                    setConfirmModel(true);
                  }}
                  onShuffleClick={() => {
                    setConfirmModelSelected(CONFIRM_ACTIONS.SHUFFLE);
                    setConfirmModel(true);
                  }}
                  isPreview={isPreview}
                />
                {!isPreview && 
                <>
                  <div className="line"></div>
                  <EditAssignmentOptionsModal
                    assignmentInfo={questions}
                    setQuestions={setQuestions}
                    selectedMcStyle={parseInt(selectedMcStyle, 10)}
                    setSelectedMcStyle={setSelectedMcStyle}
                    mcChoices={mcChoices}
                    currentQuestionId={currentQuestionId}
                    questionCourseId={questionCourseId}
                    isBlockedQuestion={isBlockedQuestion}
                    fromFlashcard={true}
                  />
                </>
                }
              </div>
              {!isPreview &&
              <div className="d-flex mt-3">
                {questionCount > 0 &&
                    <div className="cursor-pointer mr-2 mt-3 w-50 grey-icon" onClick={() => handleAssignToStudents()}>
                      <FontAwesomeIcon icon={faUserGraduate} className="px-1" />
                      Assign To Students
                    </div>
                }
                <div className={`cursor-pointer mt-3 grey-icon ${questionCount > 0 ? 'w-50' : 'w-100'}`} onClick={() => navigate(`/assignments/${Id}/manage_assign/reports/assignment_changes_report/${ActivityTypes.FlashCard}`)}>
                  <FontAwesomeIcon icon={faChartPie} className="px-1" />
                      Flashcard Changes Report
                </div>
              </div>
              }
            </div>
            <div className="d-flex flex-column w-75">
              {questionCount > 0 ?(
                <>
                  {selectedVocab && (
                    <div className="vocabulary-description-panel p-4 white-card">
                      <h5 className="mb-3 fw-normal">
                        Vocabulary <strong>{selectedQuestionIndex + 1}</strong>
                      </h5>
                      <div className="revert-tags d-block mb-3 mt-4">
                        <div className='fw-semibold' dangerouslySetInnerHTML={{
                          __html: selectedVocab.name
                        }}></div>
                      </div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: selectedVocab.description
                        }}
                      />
                    </div>
                  )}
                  <div className="d-flex justify-content-end mt-3">
                    <button
                      className="btn btn-castleWhite fs-6"
                      onClick={handleNextVocabulary}
                      disabled={questionCount <= 1 || selectedQuestionIndex === questionCount - 1}
                    >
                      Next Vocabulary
                    </button>
                  </div>
                </>
              ):(
                <>
                  {(!isLoadingPreview && !isLoadingReorder && !isLoadingRemove && !isLoadingMove) && questionCount === 0 &&
                      <div className='p-5 fs-5'>
                        There are no vocabulary terms in this activity.<br/>
                        Add vocabulary terms by clicking <strong>Add</strong> from the options on the left.
                      </div>
                  }
                </>
              )}
            </div>
          </div>
          {assignedStatus !== undefined && assignedStatus <= 3 && !isPreview && (
            <div className="scoreInfo d-flex col-12">
              <div className="scoreInfoIcon info-text">
                <span>
                  <FontAwesomeIcon icon={faInfo} />
                </span>
              </div>
              <div className="scoreInfoText justify-text">
                <p>
                      This flashcard has limited modification allowed because it has been assigned to
                      one or more students. You can delete vocabulary (if not randomized) but not add
                      new ones.&nbsp;
                  <strong>
                    <br></br>
                        If Vocabulary Options is not displayed verify that no removed students are
                        assigned by checking the box on the flashcard results page.
                  </strong>{' '}
                      If you would like to create a new flashcard using some or all of these
                      vocabulary terms, create a new flashcard, select the Add Vocabulary option, then select
                      the Your Flashcards tab, select this flashcard, then select some or all of the
                      vocabulary terms to be added to the new flashcard.
                </p>
              </div>
            </div>
          )}
        </>
      </div>

      <ConfirmationModal
        isOpen={confirmModel}
        onClose={() => setConfirmModel(false)}
        onSave={
          confirmModelSelected === CONFIRM_ACTIONS.DELETE
            ? handleRemoveQuestion
            : confirmModelSelected === CONFIRM_ACTIONS.SHUFFLE && handleReorderQuestion
        }
        contentWidth={'35%'}
        buttonPosition={'center'}
        content={
          confirmModelSelected === CONFIRM_ACTIONS.DELETE
            ? `Are you sure that you want to Remove Vocabulary ${selectedQuestionIndex + 1
            } from the Flashcard?`
            : confirmModelSelected === CONFIRM_ACTIONS.SHUFFLE &&
                  'Are you sure that you want to have the order of the Vocabulary Randomly Changed?'
        }
        cancelButton={'No'}
        confirmButton={'Yes'}
        titleName={'Confirm'}
      />
    </>
  );
};

EditActivity.propTypes = {
  isPreview: PropTypes.bool
};

export default EditActivity;